/*
Anonymize actions / aggregates so that users in one track can't see the names
and (optionally) notes / feedback of users in another track.
*/
import { create } from "mutative";

import type { ActionMessage } from "@engine/core";
import { assertGetMember } from "@engine/round/accessors";
import type { NotesAndFeedbackVisibility } from "@engine/round/actions";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { UserProfile } from "@lib/types";
import { type Track, trackNames, trackSchema } from "@lib/types";

type MinimalRoundMember = {
  userId: string;
  createdAt: string;
  track: string | Track;
  feedbackVisibleByDefault: boolean;
  evaluablePermissions: {
    [evaluableId: string]: NotesAndFeedbackVisibility;
  };
};

export type TrackMembers = {
  byTrack: Record<Track, MinimalRoundMember[]>;
  byUserId: Record<string, MinimalRoundMember>;
};

const trackInitials: Record<Track, string> = {
  MAIN: "M",
  FAIRNESS: "FA",
  FREEDOM: "FR",
  NO_TRACK: "NT",
};

const redacted = "REDACTED";

export function makeTrackMembers(round: RoundAggregate): TrackMembers {
  const roundMembers = Object.values(round.members);

  const trackMembers: TrackMembers = {
    byTrack: {
      MAIN: [],
      FAIRNESS: [],
      FREEDOM: [],
      NO_TRACK: [],
    },
    byUserId: {},
  };

  roundMembers.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  for (const member of roundMembers) {
    const track = trackSchema.parse(member.track);
    trackMembers.byTrack[track].push(member);
    trackMembers.byUserId[member.userId] = member;
  }
  return trackMembers;
}

export function canView(viewerTrack: Track, track: Track): boolean {
  return (
    viewerTrack === "NO_TRACK" || track === "NO_TRACK" || viewerTrack === track
  );
}

export function canViewAction(
  viewerTrack: Track,
  round: RoundAggregate,
  msg: ActionMessage,
): boolean {
  const action = msg.payload;
  switch (action.type) {
    case "ADD_ROUND_MEMBER":
    case "CREATE_ROUND":
      return canView(viewerTrack, action.track);
    case "PROPOSAL_DISCLOSURE_CHANGE": {
      const member = assertGetMember(round, action.userId);
      return canView(viewerTrack, member.track);
    }
    case "EVALUATION_FEEDBACK_CHANGE":
    case "EVALUATION_NOTES_CHANGE": {
      // TODO: does this break if we delete the member?
      const {
        track: memberTrack,
        feedbackVisibleByDefault,
        evaluablePermissions,
      } = assertGetMember(round, action.memberId);
      const permissionsForThisEvaluable =
        evaluablePermissions[action.evaluableId];
      if (canView(viewerTrack, memberTrack)) return true;
      if (permissionsForThisEvaluable === "hidden") return false;
      if (permissionsForThisEvaluable === "visible") return true;
      return feedbackVisibleByDefault;
    }
    case "ADD_EVALUATOR":
    case "ROUND_MEMBER_PROPERTY_CHANGE": {
      const member = assertGetMember(round, action.memberId);
      return canView(viewerTrack, member.track);
    }
    default:
      return true;
  }
}

export function anonymizeTrackMember(
  trackMembers: TrackMembers,
  userId: string,
): {
  shortname: string;
  initials: string;
} | null {
  for (const track of Object.keys(trackMembers.byTrack)) {
    const memberIndex = trackMembers.byTrack[track].findIndex(
      (member) => member.userId === userId,
    );

    if (memberIndex !== -1) {
      return {
        shortname: `${trackNames[track]} member ${memberIndex + 1}`,
        initials: trackInitials[track] + (memberIndex + 1),
      };
    }
  }
  return null;
}

export function anonymizeAction<A extends ActionMessage>(
  trackMembers: TrackMembers,
  actionMessage: A,
): A {
  return create(actionMessage, (draft) => {
    const action = draft.payload;
    switch (action.type) {
      case "ADD_ROUND_MEMBER": {
        const anonymized = anonymizeTrackMember(trackMembers, action.userId);
        action.initials = anonymized == null ? redacted : anonymized.initials;
        return;
      }
      case "CREATE_ROUND": {
        const anonymized = anonymizeTrackMember(trackMembers, action.userId);
        action.initials = anonymized == null ? redacted : anonymized.initials;
        return;
      }
      case "PROPOSAL_DISCLOSURE_CHANGE": {
        action.disclosure = redacted;
        return;
      }
      case "ADD_EVALUATOR": {
        const anonymized = anonymizeTrackMember(trackMembers, action.memberId);
        action.shortname = anonymized == null ? redacted : anonymized.shortname;
        return;
      }
      case "EVALUATION_FEEDBACK_CHANGE": {
        const permissions =
          trackMembers.byUserId[action.memberId].evaluablePermissions[
            action.evaluableId
          ];
        const visible =
          permissions === "visible"
            ? true
            : permissions === "hidden"
              ? false
              : trackMembers.byUserId[action.memberId].feedbackVisibleByDefault;

        if (!visible) {
          action.positive = redacted;
          action.negative = redacted;
        }
        return;
      }
      case "EVALUATION_NOTES_CHANGE": {
        const permissions =
          trackMembers.byUserId[action.memberId].evaluablePermissions[
            action.evaluableId
          ];
        const visible =
          permissions === "visible"
            ? true
            : permissions === "hidden"
              ? false
              : trackMembers.byUserId[action.memberId].feedbackVisibleByDefault;

        if (!visible) {
          action.notes = redacted;
        }
        return;
      }
      case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
        const anonymized = anonymizeTrackMember(trackMembers, action.memberId);
        action.initials = anonymized == null ? redacted : anonymized.initials;
        return;
      }
    }
  });
}

export function anonymizeUserProfile(
  trackMembers: TrackMembers,
  userProfile: UserProfile,
): UserProfile {
  return create(userProfile, (draft) => {
    const anonymized = anonymizeTrackMember(trackMembers, userProfile.id);
    draft.name = anonymized == null ? redacted : anonymized.shortname;
    draft.email = redacted;
    draft.picture = null;
  });
}

export function anonymizeAggregate(
  viewerTrack: Track,
  round: RoundAggregate,
): RoundAggregate {
  const trackMembers = makeTrackMembers(round);
  return create(round, (draft) => {
    for (const [userId, member] of Object.entries(draft.members)) {
      const memberTrack = trackSchema.parse(member.track);
      const anonymized = anonymizeTrackMember(trackMembers, userId);
      const canSee = canView(viewerTrack, memberTrack);
      if (!canSee) {
        member.initials = anonymized == null ? redacted : anonymized.initials;
      }

      for (const [evaluableId, feedback] of Object.entries(
        member.evaluableFeedback,
      )) {
        const permissions = member.evaluablePermissions[evaluableId];
        const visible = canSee
          ? true
          : permissions === "visible"
            ? true
            : permissions === "hidden"
              ? false
              : member.feedbackVisibleByDefault;
        if (!visible) {
          feedback.positiveNegative.positive = redacted;
          feedback.positiveNegative.negative = redacted;
        }
      }

      for (const [evaluableId] of Object.entries(member.evaluableNotes)) {
        const permissions = member.evaluablePermissions[evaluableId];
        const visible = canSee
          ? true
          : permissions === "visible"
            ? true
            : permissions === "hidden"
              ? false
              : member.feedbackVisibleByDefault;
        if (!visible) {
          member.evaluableNotes[evaluableId] = redacted;
        }
      }

      for (const evaluator of Object.values(member.evaluators)) {
        if (!canSee) {
          evaluator.shortname =
            anonymized == null ? redacted : anonymized.shortname;
        }
      }
    }

    for (const [key, disclosure] of Object.entries(draft.proposalDisclosure)) {
      const memberId = key.split("%")[1];
      const member = draft.members[memberId];
      if (!member) continue;
      const memberTrack = trackSchema.parse(member.track);
      if (!canView(viewerTrack, memberTrack)) {
        disclosure.disclosure = redacted;
      }
    }
  });
}
